// ── Layout constants ──────────────────────────────────────────────────
export const ANCHOR_SCROLL_OFFSET = -80;
export const SECTION_SX = { py: 3 } as const;
export const IMAGE_BORDER_RADIUS = 8;
export const IMAGE_LIST_COLS = 2;
export const IMAGE_LIST_GAP = 8;

// ── Timing ───────────────────────────────────────────────────────────
export const SNACKBAR_AUTO_HIDE_MS = 1500;
export const NAVIGATE_DELAY_MS = 500;
// Long enough that a search term reaches the URL once rather than once per keystroke — the input
// lives in the header now, so every commit is a `setSearchParams` and not just a `setState`.
export const SEARCH_DEBOUNCE_MS = 600;
// A full catalog sync takes ~70s, so 3s is frequent enough to feel live without polling a
// Lambda-backed endpoint hundreds of times per run.
export const SYNC_POLL_INTERVAL_MS = 3000;

// ── Contact ──────────────────────────────────────────────────────────
export const CONTACT_EMAIL = "entornobiai@gmail.com";
export const CONTACT_WHATSAPP = "573128014133";
export const WHATSAPP_URL = `https://wa.me/${CONTACT_WHATSAPP}`;

// ── WhatsApp Info Section ────────────────────────────────────────────
export const WHATSAPP_INFO = {
  mobileRepair: {
    title: "¿Necesitas reparar tu celular?",
    description: "Escríbenos por WhatsApp y agenda tu servicio de reparación.",
    message: "Hola, me gustaría obtener información sobre el servicio de reparación de celulares.",
    button: "Agendar reparación",
  },
  computerRepair: {
    title: "¿Necesitas reparar tu computador?",
    description: "Escríbenos por WhatsApp y agenda tu servicio de reparación.",
    message:
      "Hola, me gustaría obtener información sobre el servicio de reparación de computadores.",
    button: "Agendar reparación",
  },
  securityCameras: {
    title: "¿Necesitas instalar cámaras de seguridad?",
    description: "Escríbenos por WhatsApp y solicita tu cotización sin compromiso.",
    message:
      "Hola, me gustaría obtener información sobre el servicio de instalación de cámaras de seguridad.",
    button: "Solicitar cotización",
  },
} as const;

// ── Buscador del encabezado ──────────────────────────────────────────
// El término vive en la URL (`/?q=…`) y no en un estado global: así una búsqueda se puede
// compartir, el botón "atrás" la deshace, y escribir desde `/producto/:id` puede navegar al
// catálogo llevando el término consigo.
export const SEARCH_QUERY_PARAM = "q";

// Dos textos distintos porque el campo cambia de tamaño, no de función. En móvil el campo ocupa
// una fila entera y el texto largo se corta; en escritorio hay espacio para decir qué se busca.
// Ambos reemplazan las dos líneas que antes vivían en la barra inferior.
export const SEARCH_PLACEHOLDER_MOBILE = "¿Qué necesitas hoy?";
export const SEARCH_PLACEHOLDER_DESKTOP = "Buscar productos, repuestos o servicios";
export const SEARCH_ARIA_LABEL = "Buscar productos";

// Where "Enter" takes the shopper. On un celular el teclado tapa la mitad de la pantalla, así que
// cerrarlo no basta: hay que llevar la vista hasta los resultados, que están debajo del banner y
// de los servicios.
export const SEARCH_RESULTS_ANCHOR_ID = "resultados";

// ── Resultados de búsqueda ───────────────────────────────────────────
// Sin esto, una búsqueda sin coincidencias se ve igual que una que todavía está cargando: una
// grilla vacía y nada escrito. El conteo solo aparece cuando hay un filtro o una búsqueda activa;
// anunciar "2.700 resultados" en la visita normal sería ruido.
export const SEARCH_RESULTS_TEXTS = {
  count: (n: number) => (n === 1 ? "1 resultado" : `${n.toLocaleString("es-CO")} resultados`),
  empty: "No encontramos nada",
  emptyHint: "Revisa la ortografía o intenta con otra palabra.",
} as const;

// ── Bloque plegable de la página principal ───────────────────────────
// El banner y los servicios ocupan la primera pantalla completa, así que al buscar no se alcanza a
// ver ni un solo producto y no queda claro si hubo resultados. El bloque se pliega solo cuando hay
// una búsqueda activa, y se puede abrir y cerrar a mano.
export const HOME_HIGHLIGHTS_LABEL = "Promociones y servicios";
export const HOME_HIGHLIGHTS_EXPAND = "Mostrar promociones y servicios";
export const HOME_HIGHLIGHTS_COLLAPSE = "Ocultar promociones y servicios";

// ── Servicios de la página principal ─────────────────────────────────
// `label` es el nombre completo del servicio y el que se envía a analítica siempre; `shortLabel`
// solo existe porque cuatro círculos en una fila de 375px no admiten dos líneas de texto.
export const SERVICE_LINKS = [
  {
    key: "celulares",
    label: "Reparación de celulares",
    shortLabel: "Celulares",
    route: "/reparacion-de-celulares",
  },
  {
    key: "computadores",
    label: "Reparación de computadores",
    shortLabel: "Computadores",
    route: "/reparacion-de-computadores",
  },
  {
    key: "camaras",
    label: "Cámaras de seguridad",
    shortLabel: "Cámaras",
    route: "/instalacion-de-camaras-de-seguridad",
  },
  {
    key: "educacion",
    label: "Educación",
    shortLabel: "Educación",
    route: "/educacion",
  },
] as const;

// ── Filtros y barra inferior ─────────────────────────────────────────
export const FILTERS_TITLE = "Filtrar productos";
export const FILTER_ALL_LABEL = "Todos";
export const FILTER_FAVORITES_LABEL = "Favoritos";

export const FOOTER_TAGLINE = "Todo para tu tecnología en un solo lugar";
export const FOOTER_WHATSAPP_CTA = "Escríbenos";

// ── Form ─────────────────────────────────────────────────────────────
export const INPUT_LABEL_PROPS = {
  sx: {
    color: "primary.main",
    fontWeight: "bold",
    "&.Mui-focused": { color: "primary.main" },
  },
} as const;

// ── DailyBudgetPage ──────────────────────────────────────────────────
export const DAILY_BUDGET_TEXTS = {
  title: "Gestión de Presupuesto Diario",
  desktopOnly:
    "Esta página está optimizada para escritorio. Por favor, accede desde un computador para usar el módulo de contabilidad.",
  section1Title: "1. Apertura de Caja",
  section1Description:
    "Ingresa el valor en efectivo que se encuentra en el punto de venta y el valor que se encuentra en la cuenta bancaria.",
  section1Note: "Nota: verifica que el monto en caja coincida con el cierre del día anterior.",
  section2Title: "2. Análisis de resultados",
  section2Description:
    "Durante la jornada, se sugiere comprobar que los valores en la columna FINAL coincidan con los montos actuales en caja y en la cuenta bancaria.",
  section3Title: "3. Transacciones",
  section3Description:
    "Verifica las transacciones con el objetivo de encontrar posibles errores de facturación.",
} as const;

// ── ProcessOrderPage ─────────────────────────────────────────────────
export const PROCESS_ORDER_TEXTS = {
  title: "Hacer Pedido",
  contactTitle: "Datos de Contacto",
  contactDescription:
    "Para procesar tu pedido, por favor ingresa tu nombre y número de celular o WhatsApp.",
  nameLabel: "Nombre",
  phoneLabel: "Celular o Whatsapp",
  summaryTitle: "Resumen del Pedido",
  totalTitle: "Total",
  productCount: "Cantidad de productos:",
  totalLabel: "Total:",
  // La promesa solo cubre lo que ya tiene precio; decirlo evita que una línea por cotizar se lea
  // como si tuviera fecha (README §7.4).
  promiseCaveat: "para los productos con precio confirmado.",
  emptyCart: "Tu carrito está vacío. Agrega productos antes de confirmar el pedido.",
  submitButton: "Confirmar pedido",
  submitLoading: "Enviando...",
  nameRequired: "Por favor, ingresa tu nombre.",
  phoneRequired: "Por favor, ingresa tu número de celular.",
  phoneInvalid: "Por favor, ingresa un número de celular válido.",
  orderSuccess: (orderId: string) => `¡Pedido ${orderId} realizado con éxito!`,
  orderError: "Hubo un error al procesar tu pedido. Por favor, inténtalo de nuevo.",
} as const;

// ── OrderPage ────────────────────────────────────────────────────────
export const ORDER_TEXTS = {
  title: "Pedido Online",
  clientLabel: "Cliente:",
  phoneLabel: "Teléfono:",
  dateLabel: "Fecha:",
  statusLabel: "Estado:",
  totalLabel: "Total a pagar:",
  quantityLabel: "Cantidad:",
  loading: "Cargando...",
  notFound: "Error: Pedido no encontrado",

  // ── EB-548: la pantalla de confirmación ────────────────────────────
  confirmedTitle: "¡Recibimos tu pedido!",
  confirmedSubtitle:
    "Un asesor te confirma disponibilidad y forma de pago por WhatsApp. Guarda este enlace: es el detalle de tu pedido.",
  deliveryTitle: "Entrega",
  deliveryTo: (municipality: string) => `Envío a domicilio en ${municipality}`,
  pickupAt: (address: string) => `Recoges en tienda: ${address}`,
  addressLabel: "Dirección:",
  instructionsLabel: "Indicaciones:",
  promiseLabel: "Entrega estimada:",
  priceHold: (date: string) => `Precio garantizado hasta el ${date}`,
  // El servidor recalcula el dinero; si no coincide con lo que el navegador envió, gana el
  // servidor y hay que decirlo — callarlo sería cobrar un número que el cliente nunca vio.
  totalsAdjusted:
    "Ajustamos el total con las tarifas vigentes. El valor de abajo es el que la tienda respeta.",
  whatsappButton: "Continuar por WhatsApp",
  whatsappHint: "Te llevamos al chat con el asesor con el número de tu pedido.",
} as const;

// ── ProductDetailPage ────────────────────────────────────────────────
export const PRODUCT_DETAIL_TEXTS = {
  errorLoading: "Ocurrió un error cargando el producto.",
  notFound: "Producto no encontrado.",
  // Carries its colon because it labels the prose that follows it, the way "Precio:" labels the
  // number. `specsTitle` heads a table and does not.
  descriptionTitle: "Descripción:",
  specsTitle: "Especificaciones",
  relatedProductsTitle: "Productos relacionados",
  carouselRoleDescription: "carrusel",
  carouselPrevious: "Imagen anterior",
  carouselNext: "Imagen siguiente",
  carouselGoTo: (position: number) => `Ver imagen ${position}`,
  available: "Disponible",
  quantityLabel: "Cantidad",
  quantityValue: (quantity: number) => `Cantidad: ${quantity}`,
  addToCart: "Agregar al carrito",
  buyNow: "Comprar ahora",
  carouselPosition: (position: number, total: number) => `Imagen ${position} de ${total}`,
  relatedProductError: "Error al cargar producto",
} as const;

// ── ItemMaintenancePage ──────────────────────────────────────────────
export const ITEM_MAINTENANCE_TEXTS = {
  missingId: "Falta el parámetro id.",
  notFound: "No se encontró el ítem.",
  loading: "Cargando...",
  formTitle: "Mantenimiento del Ítem",
  uploading: "Subiendo...",
  updateMainImage: "Actualizar Imagen Principal",
  addGalleryImage: "Agregar Imagen a Galería",
  previewLabel: "Vista previa subida:",
  imagesLabel: (count: number) => `Imágenes (${count})`,
  noImages: "No hay imágenes agregadas aún.",
  saving: "Guardando...",
  save: "Guardar",
  numericError: "Precio y descuento deben ser valores numéricos.",
  saveError: "Error guardando el ítem. Intenta nuevamente.",
  notFoundError: "Este ítem ya no existe. Es posible que haya sido eliminado en otra sesión.",
  deleteButton: "Eliminar Item",
  deleteSuccess: "Item eliminado correctamente.",
  deleteError: "Error eliminando el ítem. Intenta nuevamente.",
} as const;

// ── ItemListPage ────────────────────────────────────────────────────
export const ITEM_LIST_TEXTS = {
  pageTitle: "Mantenimiento de Items",
  createButton: "Crear Item",
  deleteConfirmTitle: "Confirmar eliminación",
  deleteConfirmMessage: (name: string) => `¿Estás seguro de que deseas eliminar "${name}"?`,
  deleteButton: "Eliminar",
  cancelButton: "Cancelar",
  deleteSuccess: "Item eliminado correctamente.",
  deleteError: "Error eliminando el ítem. Intenta nuevamente.",
  colId: "ID",
  colName: "Nombre",
  colPrice: "Precio",
  colQuantity: "Cantidad",
  colActive: "Activo",
  colDiscount: "Descuento",
  colTipo: "Tipo",
  colRating: "Rating",
  colMarca: "Marca",
  colModelo: "Modelo",
  colCondicion: "Condición",
  colRelacionados: "Relacionados",
  colFuente: "Fuente",
  colFavoritos: "Favoritos",
  colActions: "Acciones",
  activeYes: "Sí",
  activeNo: "No",
  breadcrumb: "Items",
  saveSuccess: "Item actualizado correctamente.",
  saveError: "Error actualizando el ítem.",
  syncButton: "Sincronizar con Alegra",

  // ── Filtros de curación (EB-509) ──
  // La cola es el resultado real de una sincronización: los productos entran inactivos y no se ven
  // en la tienda hasta que alguien los completa. Sin estos filtros esa cola es invisible.
  filterAll: "Todos",
  filterPending: "Pendientes de curar",
  filterInactive: "Inactivos",
  filterRecent: "Sincronizados recientemente",
  filterRecentTooltip: "Modificados en los últimos 7 días",
  filterPendingTooltip: "Productos creados por la sincronización que siguen inactivos",
  filterCount: (n: number) => n.toLocaleString("es-CO"),
} as const;

// ── Selector de productos relacionados (EB-511) ──────────────────────
export const RELATED_PRODUCTS_TEXTS = {
  searchLabel: "Buscar producto",
  // El conteo va en el texto de ayuda a propósito: la lista muestra como máximo
  // RELATED_OPTIONS_LIMIT opciones, así que sin esto alguien podría creer que el catálogo entero
  // son las ~50 filas que alcanza a ver.
  searchHelper: (total: number, limit: number) =>
    total > limit
      ? `Escribe para filtrar entre ${total.toLocaleString("es-CO")} productos (se muestran ${limit} a la vez)`
      : `${total.toLocaleString("es-CO")} productos disponibles`,
  noOptions: "Ningún producto coincide",
} as const;

// ── Sincronización con Alegra (EB-498) ───────────────────────────────
export const ALEGRA_SYNC_TEXTS = {
  confirmTitle: "Sincronizar el catálogo con Alegra",
  confirmIntro:
    "Se leerán todos los productos activos de Alegra y se crearán o actualizarán en el catálogo. " +
    "Puede tardar un par de minutos.",
  confirmScope:
    "La sincronización solo escribe nombre, precio y cantidad disponible. Las imágenes, " +
    "descripciones, especificaciones y demás datos curados a mano no se tocan.",
  confirmInactive:
    "Los productos nuevos entran INACTIVOS: no se publican en la tienda hasta que alguien los cure.",
  confirmButton: "Sincronizar",
  cancelButton: "Cancelar",
  closeButton: "Cerrar",

  runningTitle: "Sincronización en curso",
  // The worker reports the phase it is in; the admin sees plain Spanish instead of the raw status.
  statusLabels: {
    queued: "En cola...",
    scanning: "Leyendo el catálogo actual...",
    fetching: "Consultando productos en Alegra...",
    writing: "Guardando cambios...",
    completed: "Finalizada",
    failed: "Fallida",
    rejected: "Rechazada",
  } as Record<string, string>,
  progressFetched: (fetched: number) => `${fetched.toLocaleString("es-CO")} productos leídos`,
  progressScanned: (scanned: number) => `${scanned.toLocaleString("es-CO")} filas revisadas`,
  runningHint: "Puedes cerrar esta ventana: la sincronización sigue corriendo.",

  summaryTitle: "Resumen de la sincronización",
  // Leads the summary deliberately: new products land inactive, so the queue they form is the
  // real result of the run, not a footnote (plan §4.3.1).
  pendingCuration: (count: number) =>
    count === 1
      ? "1 producto nuevo pendiente de curar"
      : `${count.toLocaleString("es-CO")} productos nuevos pendientes de curar`,
  pendingCurationHint:
    "Entraron inactivos. No se ven en la tienda hasta que se publiquen desde esta página.",
  // A simulation writes nothing, so its counters are all zero — the numbers worth showing are the
  // planned ones. Saying "0 productos nuevos" after a dry run would contradict the whole point of
  // running one.
  pendingCurationDryRun: (count: number) =>
    count === 1
      ? "1 producto nuevo entraría pendiente de curar"
      : `${count.toLocaleString("es-CO")} productos nuevos entrarían pendientes de curar`,
  labelCreated: "Creados",
  labelUpdated: "Actualizados",
  labelCreatedDryRun: "Se crearían",
  labelUpdatedDryRun: "Se actualizarían",
  labelSkipped: "Sin cambios u omitidos",
  labelFailed: "Con error",
  labelScanned: "Filas revisadas",
  labelFetched: "Leídos de Alegra",
  dryRunNotice: "Simulación: no se escribió nada en el catálogo.",
  vanishedTitle: (count: number) =>
    count === 1
      ? "1 producto ya no está activo en Alegra"
      : `${count.toLocaleString("es-CO")} productos ya no están activos en Alegra`,
  vanishedHint:
    "No se desactivaron automáticamente: revísalos y decide si siguen publicados. " +
    "Mientras tanto conservan su último precio y existencias.",

  // ── Cambios de disponibilidad (EB-549) ──
  // "Actualizados: 40" no dice si la tienda cambió. Esto sí. Solo aparecen productos visibles:
  // una fila inactiva resuelve a UNAVAILABLE en los dos lados del diff y no produce transición.
  transitionsTitle: "Cambios de disponibilidad en la tienda",
  transitionsHint:
    "Se calculan a partir del precio y las existencias que trajo Alegra. Los productos " +
    "inactivos no cuentan: no se ven en la tienda.",
  transitionLabels: {
    "IN_STOCK>BACKORDER": "Se agotaron y pasaron a bajo pedido",
    "BACKORDER>IN_STOCK": "Volvieron a estar disponibles",
    "IN_STOCK>QUOTE": "Perdieron el precio y quedaron por cotizar",
    "BACKORDER>QUOTE": "Quedaron por cotizar",
    "QUOTE>IN_STOCK": "Ya tienen precio y existencias",
    "QUOTE>BACKORDER": "Ya tienen precio, bajo pedido",
  } as Record<string, string>,

  errorAlreadyRunning:
    "Ya hay una sincronización en curso. Espera a que termine antes de lanzar otra.",
  errorForbidden: "No tienes permisos de administrador para sincronizar el catálogo.",
  errorStartFailed: "No se pudo iniciar la sincronización. Intenta nuevamente.",
  errorPollFailed: "Se perdió el seguimiento de la sincronización. Puede que siga corriendo.",
  // A failed run keeps its partial counts on purpose, so the summary is still worth showing.
  errorJobFailed: (reason: string | null) =>
    reason
      ? `La sincronización falló: ${reason}. Los conteos de abajo son parciales.`
      : "La sincronización falló. Los conteos de abajo son parciales.",
} as const;

// ── ItemCreateForm ───────────────────────────────────────────────────
export const ITEM_CREATE_TEXTS = {
  formTitle: "Datos del nuevo ítem",
  pageTitle: "Crear nuevo ítem",
  idLabel: "ID del Ítem",
  imagesTitle: "Imágenes del ítem",
  mainImageButton: "Imagen principal",
  galleryButton: "Agregar a galería",
  previewLabel: "Vista previa principal:",
  galleryLabel: (count: number) => `Galería (${count})`,
  noImages: "No hay imágenes agregadas aún.",
  idRequired: "Debes ingresar el ID del ítem antes de subir imágenes.",
  requiredFields: "ID, nombre y precio son obligatorios.",
  numericError: "Precio y descuento deben ser valores numéricos.",
  createButton: "Crear ítem",
  saving: "Guardando...",
  success: "Ítem creado correctamente.",
  createError: "Error creando el ítem. Intenta nuevamente.",
  duplicateIdError:
    "Ya existe un ítem con este ID. Cambia el ID para no sobrescribir el producto existente.",
  uploadMainError: "Error subiendo la imagen principal.",
  uploadGalleryError: "Error subiendo la imagen de galería.",
} as const;

// ── Discount validation ──────────────────────────────────────────────
export const DISCOUNT_RANGE_ERROR = "El descuento debe estar entre 0 y 100.";

// ── Disponibilidad (docs/ordering-flow/README.md §3.3) ───────────────
// Los dos valores que el negocio administra desde /delivery-maintenance. Estos son el respaldo
// para cuando la configuración todavía no cargó: la página del producto no puede quedarse sin
// prometer nada mientras llega un JSON.
export const DEFAULT_LEAD_TIME_DAYS = 5;
export const DEFAULT_PRICE_HOLD_DAYS = 15;

// Una promesa, nunca un conteo de existencias: "llega en 5 días hábiles" es un compromiso que la
// tienda puede cumplir; "quedan 3 unidades" es un número de una sincronización semanal contra una
// tienda física, y deja de ser cierto apenas alguien compra uno en el mostrador.
export const AVAILABILITY_TEXTS = {
  chip: {
    IN_STOCK: "Disponible",
    BACKORDER: "Bajo pedido",
    QUOTE: "A cotizar",
    UNAVAILABLE: "No disponible",
  } as Record<string, string>,

  // Bloque de compra
  inStockDetail: "Entrega inmediata en tienda",
  backorderDetail: (days: number) =>
    days === 0
      ? "Lo conseguimos para ti · Llega el mismo día"
      : days === 1
        ? "Lo conseguimos para ti · Llega en 1 día hábil"
        : `Lo conseguimos para ti · Llega en ${days} días hábiles`,
  // Se escribe aparte porque sin esto "bajo pedido" se lee como "quizá te cobremos más después".
  priceGuarantee: (days: number) => `Precio garantizado por ${days} días`,
  quotePrice: "Precio a cotizar",
  quoteDetail: "Lo conseguimos para ti. Te confirmamos el precio por WhatsApp antes de cobrarte.",

  // Acciones: los mismos dos botones y los mismos dos destinos, tres vocabularios.
  buyNow: {
    IN_STOCK: "Comprar ahora",
    BACKORDER: "Pedir ahora",
    QUOTE: "Solicitar cotización",
  } as Record<string, string>,
} as const;

// ── Carrito ──────────────────────────────────────────────────────────
export const CART_TEXTS = {
  quoteLine: "Por cotizar",
  increase: "Aumentar cantidad",
  decrease: "Disminuir cantidad",
  remove: "Quitar del carrito",
  // "Total" a secas sería mentira cuando falta cotizar una línea: el número no incluye todo lo
  // que el cliente se está llevando. El rótulo cambia; el número no.
  total: "Total:",
  partialTotal: "Total parcial:",
  subtotalLabel: "Subtotal:",
  deliveryLabel: "Envío:",
  quoteNotice: (count: number) =>
    count === 1
      ? "+ 1 producto por cotizar. Te confirmamos su precio por WhatsApp antes de cobrarte."
      : `+ ${count} productos por cotizar. Te confirmamos sus precios por WhatsApp antes de cobrarte.`,
  // Written exactly like the page it lands on (`PROCESS_ORDER_TEXTS.title`): a button that renames
  // its own destination makes the shopper wonder whether they arrived somewhere else.
  checkout: "Hacer Pedido",
  empty: "Vaciar",
} as const;

// ── Shared form labels ───────────────────────────────────────────────
export const FORM_LABELS = {
  name: "Nombre",
  description: "Descripción",
  availableQuantity: "Cantidad disponible",
  leadTimeDays: "Días para conseguirlo",
  // Shown under the field because the consequence of leaving it blank is not obvious from the
  // label: an empty field is not "no promise", it is "the default promise".
  leadTimeDaysHelp: "Solo aplica sin existencias. Vacío = días por defecto.",
  discount: "Descuento",
  active: "Activo",
  price: "Precio",
  tipoProducto: "Tipo de producto",
  marca: "Marca",
  modelo: "Modelo",
  variante: "Variante",
  condicion: "Condición",
  garantiaMeses: "Garant��a (meses)",
  precioReferenciaCop: "Precio referencia (COP)",
  urlOficial: "URL oficial",
  metadataAccordion: "Detalles adicionales",
  specsAccordion: "Especificaciones del celular",
  relatedProductsSection: "Productos relacionados",
} as const;

// ── Product relation labels ──────────────────────────────────────────
export const RELATION_LABELS: Record<string, string> = {
  repuesto: "Repuesto",
  accesorio: "Accesorio",
  compatible_con: "Compatible",
  variante: "Variante",
  sucesor: "Sucesor",
};

// ── Celular spec labels ─────────────────────────────────────────────
export const CELULAR_SPEC_LABELS: Record<string, string> = {
  procesador: "Procesador",
  sistema_operativo: "Sistema operativo",
  ram: "RAM",
  almacenamiento: "Almacenamiento",
  memoria_expandible: "Memoria expandible",
  tipo_pantalla: "Tipo de pantalla",
  tamano_pantalla: "Tamaño de pantalla",
  propiedades_camara: "Cámara",
  bateria: "Batería",
  factor_forma: "Factor de forma",
  ano_lanzamiento: "Año de lanzamiento",
  precio_lanzamiento_usd: "Precio de lanzamiento (USD)",
  ranking_2025: "Ranking 2025",
  ranking_historico: "Ranking histórico",
  unidades_vendidas_estimadas: "Unidades vendidas estimadas",
};

// ── Product category labels ─────────────────────────────────────────
export const CATEGORY_LABELS: Record<string, string> = {
  celular: "Celulares",
  vidrio: "Vidrios",
  display: "Display",
  bateria: "Baterías",
  visor: "Visores",
  flex: "Flexes",
  protector_camara: "Protectores de Cámara",
  tapa_trasera: "Tapas Traseras",
  auricular: "Auriculares",
  altavoz: "Altavoces",
  camara: "Cámaras",
  tablet: "Tablets",
  otros: "Otros",
};

// ── EducationPage ────────────────────────────────────────────────────
export const EDUCATION_PAGE_TEXTS = {
  title: "Educación",
  subtitle: "Programas y cursos de formación tecnológica",
  description:
    "En Entorno BIAI ofrecemos programas educativos diseñados para fortalecer tus habilidades en tecnología. Explora nuestros cursos disponibles e inscríbete para comenzar tu formación.",
  loginPrompt: "Inicia sesión para inscribirte en nuestros programas educativos.",
  loginButton: "Iniciar Sesión",
  coursesTitle: "Programas y Cursos Disponibles",
  noCourses: "No hay programas disponibles en este momento.",
  coursesLoading: "Cargando programas...",
  coursesError: "Error cargando los programas educativos.",
  myEnrollmentsTitle: "Mis Inscripciones",
  noEnrollments: "Aún no estás inscrito en ningún programa.",
  enrollmentsLoading: "Cargando inscripciones...",
  enrollButton: "Inscribirme",
  enrolling: "Inscribiendo...",
  enrollSuccess: "¡Inscripción exitosa!",
  enrollError: "Error al inscribirse. Inténtalo de nuevo.",
  alreadyEnrolled: "Ya inscrito",
  whatsappMessage: "Hola, estoy interesado en los programas de Educación de Entorno BIAI.",
  whatsappButton: "Contactar por WhatsApp",
  categoryProgram: "Programa",
  categoryCourse: "Curso",
  statusEnrolled: "Inscrito",
  statusCompleted: "Completado",
  statusDropped: "Retirado",
  duration: "Duración",
  dates: "Fechas",
  capacity: "Cupos",
  detailNotFound: "Programa no encontrado.",
  detailError: "Error cargando el programa.",
  backToList: "Volver a programas",
  moreInfoTitle: "¿Necesitas más información?",
  moreInfoDescription:
    "Escríbenos por WhatsApp y te asesoramos sobre nuestros programas y cursos de formación.",
  moreInfoWhatsappMessage:
    "Hola, me gustaría obtener más información sobre los programas educativos de Entorno BIAI.",
  moreInfoWhatsappButton: "Obtener más información",
} as const;

// ── BannerMaintenancePage ────────────────────────────────────────────
export const BANNER_LIST_TEXTS = {
  pageTitle: "Mantenimiento de Banners",
  createButton: "Crear Banner",
  colId: "ID",
  colTitle: "Título",
  colLinkUrl: "URL destino",
  colOrder: "Orden",
  colActive: "Activo",
  colActions: "Acciones",
  saveSuccess: "Banner actualizado correctamente.",
  saveError: "Error actualizando el banner.",
  deleteSuccess: "Banner eliminado correctamente.",
  deleteError: "Error eliminando el banner.",
  deleteConfirmTitle: "Confirmar eliminación",
  deleteConfirmMessage: (title: string) => `¿Estás seguro de que deseas eliminar "${title}"?`,
  deleteButton: "Eliminar",
  cancelButton: "Cancelar",
} as const;

export const BANNER_FORM_TEXTS = {
  createTitle: "Datos del nuevo banner",
  createPageTitle: "Crear nuevo banner",
  editTitle: "Mantenimiento del Banner",
  idLabel: "ID del Banner",
  titleLabel: "Título",
  altTextLabel: "Texto alternativo",
  linkUrlLabel: "URL destino (dejar vacío si no es clickeable)",
  displayOrderLabel: "Orden de visualización",
  activeLabel: "Activo",
  uploadImage: "Subir imagen del banner",
  uploading: "Subiendo...",
  previewLabel: "Vista previa:",
  save: "Guardar",
  saving: "Guardando...",
  createButton: "Crear banner",
  loading: "Cargando...",
  notFound: "No se encontró el banner.",
  saveError: "Error guardando el banner.",
  requiredFields: "ID, título e imagen son obligatorios.",
  numericError: "El orden debe ser un valor numérico.",
  duplicateIdError:
    "Ya existe un banner con este ID. Cambia el ID para no sobrescribir el banner existente.",
  notFoundError: "Este banner ya no existe. Es posible que haya sido eliminado en otra sesión.",
} as const;

// ── EducationItemMaintenancePage (Phase 3 — EB-482) ──────────────────
export const EDUCATION_ITEM_LIST_TEXTS = {
  pageTitle: "Mantenimiento de Cursos",
  createButton: "Crear Curso",
  colSlug: "Slug",
  colId: "ID",
  colName: "Nombre",
  colCategory: "Categoría",
  colStatus: "Estado",
  colCapacity: "Cupo",
  colActions: "Acciones",
  deleteSuccess: "Curso eliminado correctamente.",
  deleteError: "Error eliminando el curso.",
  deleteConfirmTitle: "Confirmar eliminación",
  deleteConfirmMessage: (name: string) => `¿Estás seguro de que deseas eliminar "${name}"?`,
  deleteButton: "Eliminar",
  cancelButton: "Cancelar",
  loading: "Cargando cursos...",
  empty: "No hay cursos disponibles.",
} as const;

export const EDUCATION_ITEM_FORM_TEXTS = {
  createPageTitle: "Crear nuevo curso",
  createTitle: "Datos del nuevo curso",
  editTitle: "Mantenimiento del Curso",
  slugLabel: "Slug (identificador en la URL)",
  idLabel: "ID numérico",
  nameLabel: "Nombre",
  descriptionLabel: "Descripción",
  statusLabel: "Estado",
  categoryLabel: "Categoría",
  startDateLabel: "Fecha de inicio",
  endDateLabel: "Fecha de fin",
  durationLabel: "Duración",
  maxCapacityLabel: "Cupo máximo",
  pictureLabel: "URL de la imagen",
  save: "Guardar",
  saving: "Guardando...",
  createButton: "Crear curso",
  loading: "Cargando...",
  notFound: "No se encontró el curso.",
  saveError: "Error guardando el curso.",
  requiredFields: "Slug, ID y nombre son obligatorios.",
  numericError: "El ID y el cupo deben ser numéricos.",
  statusActive: "Activo",
  statusInactive: "Inactivo",
  statusDraft: "Borrador",
  categoryProgram: "Programa",
  categoryCourse: "Curso",
} as const;

// ── AlegraItemsPage ──────────────────────────────────────────────────
export const ALEGRA_ITEMS_TEXTS = {
  pageTitle: "Items de Alegra",
  colId: "ID",
  colReference: "Referencia",
  colName: "Nombre",
  colDescription: "Descripción",
  colStatus: "Estado",
  colPrice: "Precio",
  colUnit: "Unidad",
  loading: "Cargando items de Alegra...",
  noRows: "No hay items disponibles.",
  loadError: "Error cargando los items de Alegra. Intenta nuevamente.",
  prevPage: "Anterior",
  nextPage: "Siguiente",
  pageIndicator: (page: number, totalPages: number) => `Página ${page} de ${totalPages}`,
  totalItems: (total: number) => `${total.toLocaleString("es-CO")} items en total`,
} as const;

// ── Entrega (tienda) ─────────────────────────────────────────────────
// El envío se cotiza en la página de producto, no al final del checkout: un costo inesperado en la
// última pantalla es la principal causa de abandono que existe (README §5.5).
export const DELIVERY_TEXTS = {
  shipTo: "Enviar a:",
  municipalityLabel: "Municipio",
  municipalityPlaceholder: "Escribe o elige tu municipio",
  municipalityNoMatch: "Ese municipio no está en nuestra lista de entregas",
  freeShipping: "Envío gratis",
  feeValue: (fee: string) => `Envío ${fee}`,
  promise: (min: number, max: number) =>
    min === max
      ? max === 0
        ? "Llega hoy mismo"
        : max === 1
          ? "Llega en 1 día hábil"
          : `Llega en ${max} días hábiles`
      : `Llega en ${min}–${max} días hábiles`,
  // Un carrito solo de cotizaciones no tiene fecha: decirlo es más honesto que prometer los días
  // de la zona para algo que nadie ha cotizado.
  promiseUnknown: "Te confirmamos la fecha por WhatsApp",

  // Checkout — sección Entrega
  sectionTitle: "Entrega",
  methodLabel: "¿Cómo quieres recibirlo?",
  methodDelivery: "Envío a domicilio",
  methodPickup: "Recoger en tienda",
  pickupAt: (address: string) => `Recoges en ${address}`,
  addressLabel: "Dirección",
  addressPlaceholder: "Calle, número, barrio",
  instructionsLabel: "Indicaciones para la entrega (opcional)",
  instructionsPlaceholder: "Apartamento, punto de referencia, horario",
  municipalityRequired: "Elige el municipio de entrega.",
  addressRequired: "Ingresa la dirección de entrega.",
  freeShippingFrom: (amount: string) => `Envío gratis desde ${amount}`,
} as const;

// ── DeliveryMaintenancePage ──────────────────────────────────────────
export const DELIVERY_ZONE_TEXTS = {
  pageTitle: "Zonas de entrega",
  createButton: "Crear zona",
  seedButton: "Sembrar municipios",
  seeding: "Sembrando...",
  searchPlaceholder: "Buscar municipio...",
  colId: "ID",
  colMunicipality: "Municipio",
  colZone: "Zona",
  colFee: "Tarifa",
  colThreshold: "Envío gratis desde",
  colEtaMin: "Días mín.",
  colEtaMax: "Días máx.",
  colOrder: "Orden",
  colActive: "Activo",
  colActions: "Acciones",
  loading: "Cargando zonas de entrega...",
  // The empty grid is the one state with an action attached: nothing has been seeded on this stage.
  noRows: "No hay zonas de entrega. Usa “Sembrar municipios” para cargar la lista inicial.",
  loadError: "Error cargando las zonas de entrega.",
  saveSuccess: "Zona actualizada correctamente.",
  saveError: "Error actualizando la zona.",
  deleteSuccess: "Zona eliminada correctamente.",
  deleteError: "Error eliminando la zona.",
  deleteConfirmTitle: "Confirmar eliminación",
  deleteConfirmMessage: (municipality: string) =>
    `¿Eliminar “${municipality}”? Los pedidos ya realizados a este municipio conservan su copia; ` +
    `si solo quieres dejar de entregar allí, desactívalo en lugar de eliminarlo.`,
  deleteButton: "Eliminar",
  cancelButton: "Cancelar",
  seedSuccess: (created: number, skipped: number) =>
    skipped > 0
      ? `${created} municipios creados, ${skipped} ya existían y no se tocaron.`
      : `${created} municipios creados.`,
  seedError: "Error sembrando los municipios.",
  createTitle: "Crear zona de entrega",
  idLabel: "ID (slug)",
  idHelp: "Minúsculas, números y guiones. Ejemplo: santa-rosa-de-osos",
  municipalityLabel: "Municipio",
  zoneLabel: "Zona",
  zoneHelp: "Agrupación para mostrar. Puedes reutilizar una existente o escribir una nueva.",
  feeLabel: "Tarifa (COP)",
  thresholdLabel: "Envío gratis desde (COP)",
  thresholdHelp: "Vacío = sin umbral. 0 = siempre gratis.",
  etaMinLabel: "Días mínimos",
  etaMaxLabel: "Días máximos",
  etaHelp: "Días hábiles que se suman al tiempo de consecución del producto.",
  orderLabel: "Orden en el selector",
  activeLabel: "Activo",
  createSubmit: "Crear zona",
  creating: "Creando...",
  createSuccess: "Zona creada correctamente.",
  createError: "Error creando la zona.",
  createErrorDuplicate: "Ya existe una zona con ese ID.",
  requiredFields: "El ID y el municipio son obligatorios.",
  invalidId: "El ID solo admite minúsculas, números y guiones.",
  invalidNumbers: "La tarifa y los días deben ser números mayores o iguales a 0.",
  invalidEtaOrder: "Los días mínimos no pueden superar los días máximos.",
  configTitle: "Configuración general",
  configSubtitle: "Aplica a toda la tienda, no a un municipio.",
  configLeadTimeLabel: "Días de entrega por defecto",
  configLeadTimeHelp: "La promesa de los productos bajo pedido que no tienen días propios.",
  configPriceHoldLabel: "Días de precio garantizado",
  configPickupLabel: "Recoger en tienda habilitado",
  configAddressLabel: "Dirección de la tienda",
  configSave: "Guardar configuración",
  configSaving: "Guardando...",
  configSaveSuccess: "Configuración actualizada correctamente.",
  configSaveError: "Error actualizando la configuración.",
  // A 404 here means the `__config__` row does not exist yet: the stage was never seeded.
  configMissing: "La fila de configuración no existe todavía. Usa “Sembrar municipios” primero.",
} as const;

// ── NotFoundPage ─────────────────────────────────────────────────────
export const NOT_FOUND_TEXT = "Not Found";

// ── Cookie consent (EB-559) ──────────────────────────────────────────
// Every string the shopper reads about cookies lives here, so the banner, the preferences dialog
// and the privacy policy cannot drift into saying different things about the same cookies.
export const CONSENT_TEXTS = {
  bannerTitle: "Usamos cookies para entender cómo se usa la tienda",
  bannerBody:
    "Las cookies necesarias para tu sesión y tu carrito siempre están activas. Las de analítica " +
    "(Google Analytics) solo se activan si las aceptas, y puedes cambiar de opinión cuando quieras.",
  accept: "Aceptar analítica",
  reject: "Solo las necesarias",
  policyLink: "Ver política de cookies",

  prefsTitle: "Preferencias de cookies",
  prefsNecessaryTitle: "Necesarias",
  prefsNecessaryBody:
    "Mantienen tu sesión iniciada, tu carrito y el municipio de entrega que elegiste. Sin ellas la " +
    "tienda no funciona, así que no se pueden desactivar.",
  prefsAnalyticsTitle: "Analítica",
  prefsAnalyticsBody:
    "Google Analytics, con las cookies _ga y _ga_<ID>. Nos dicen qué productos se ven y en qué paso " +
    "se abandona un pedido. Si las rechazas no se carga nada de Google y se borran las que hubiera.",
  prefsAnalyticsLabel: "Permitir cookies de analítica",
  prefsDecidedOn: (date: string) => `Tu elección actual se guardó el ${date}.`,
  prefsNeverDecided: "Todavía no has elegido. Por ahora la analítica está desactivada.",
  prefsSave: "Guardar preferencias",
  prefsClose: "Cerrar",
  footerLink: "Preferencias de cookies",
} as const;
